import { msg } from '@lingui/core/macro';
import { brandNames } from 'data/schema';
import type { IBrand, IMarket } from 'data/schema';
import { marketNames } from 'containers/SelfService/marketFlags';
import type {
  IDealerResolutionInput,
  IDealerSelectionError,
  TDealerResolution,
  TDealerSelectionValidation,
} from './dealerResolution.types';
import { getEligibleDealerIds } from './dealerEligibility';
import { validateDealerId } from './dealerIdValidator';

export { getEligibleDealerIds } from './dealerEligibility';

interface IDealerSelectionContext {
  dealerIds: readonly string[];
  brand: IBrand;
  market: IMarket;
}

/** Thin adapter over validateDealerId (AVS-4406): format, then ownership, then brand/market, each with a distinct reason. */
export function validateDealerSelection(
  dealerId: string,
  { dealerIds, brand, market }: IDealerSelectionContext
): TDealerSelectionValidation {
  const validation = validateDealerId(dealerId, { dealerIds, brand, market });

  if (validation.ok) {
    return { ok: true };
  }

  return { ok: false, error: { reason: validation.error.reason, dealerId: validation.error.dealerId } };
}

/**
 * Decides which dealer applies after login.
 * Precedence: URL dealer ID, then the stored selection, then the session's own dealer ID
 * (a hint, silently skipped when invalid), then auto/choose/none by eligible count.
 * A rejected URL or stored ID attaches its error and shows the step, even with one eligible ID.
 */
export function resolveDealerForLogin(input: IDealerResolutionInput): TDealerResolution {
  const { dealerIds, brand, market, urlDealerId, storedDealerId, sessionDealerId } = input;
  const context = { dealerIds, brand, market };
  const eligible = getEligibleDealerIds(dealerIds, brand, market);

  if (urlDealerId) {
    const validation = validateDealerSelection(urlDealerId, context);

    if (validation.ok) {
      return { status: 'selected', dealerId: urlDealerId, source: 'url' };
    }

    return eligible.length > 0 ? { status: 'choose', options: eligible, error: validation.error } : { status: 'none' };
  }

  if (storedDealerId) {
    const validation = validateDealerSelection(storedDealerId, context);

    if (validation.ok) {
      return { status: 'selected', dealerId: storedDealerId, source: 'stored' };
    }

    return eligible.length > 0 ? { status: 'choose', options: eligible, error: validation.error } : { status: 'none' };
  }

  if (sessionDealerId && validateDealerSelection(sessionDealerId, context).ok) {
    return { status: 'selected', dealerId: sessionDealerId, source: 'session' };
  }

  if (eligible.length === 0) {
    return { status: 'none' };
  }

  if (eligible.length === 1) {
    return { status: 'selected', dealerId: eligible[0], source: 'auto' };
  }

  return { status: 'choose', options: eligible };
}

/** Human-readable brand and market, e.g. "Audi Netherlands"; falls back to the raw codes when unknown. */
export function getBrandMarketLabel(brand: IBrand, market: IMarket): string {
  return `${brandNames[brand] ?? brand} ${marketNames[market] ?? market}`;
}

/** Message for when the user owns no dealer ID matching the URL's brand and market. */
export function getNoMatchingDealerMessage(brand: IBrand, market: IMarket) {
  const brandAndMarket = getBrandMarketLabel(brand, market);

  return msg({
    message: `None of your Dealer IDs is valid for ${brandAndMarket}. Contact your administrator to request access.`,
    comment: 'brandAndMarket is a brand and country name, for example "Audi Netherlands"',
  });
}

/** Message shown in the dealer selection dialog for a rejected dealer ID. */
export function getDealerSelectionErrorMessage(error: IDealerSelectionError, brand: IBrand, market: IMarket) {
  const { dealerId } = error;

  switch (error.reason) {
    case 'not-owned':
      return msg({
        message: `Dealer ID ${dealerId} does not belong to your account. Select one of your Dealer IDs below.`,
      });
    case 'invalid-format':
      return msg({
        message: `Dealer ID ${dealerId} is not in a valid format. Select one of your Dealer IDs below.`,
      });
    case 'brand-market-mismatch': {
      const brandAndMarket = getBrandMarketLabel(brand, market);

      return msg({
        message: `Dealer ID ${dealerId} is not valid for ${brandAndMarket}. Select one of the Dealer IDs below.`,
        comment: 'brandAndMarket is a brand and country name, for example "Audi Netherlands"',
      });
    }
  }
}
